import Box from '@mui/material/Box';
import { getPokerMin } from '@/components/images/base64/PokerMin';
import { BetRoomSeatIndex } from '@/models/games/BetRoom';
import { BlackjackBetType, BlackjackResult } from '@/models/games/blackjack';
import { getMainPlayerState } from '@/modules/main/selector';
import { getBlackjackPoints, getDisplaySeat } from '@/utils/games/blackjack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BlackjackResultRecordPanelProps } from './BlackjackResultRecordPanel';

export const BlackjackResultDetailPanel = (
    props: BlackjackResultRecordPanelProps
) => {
    const { bet } = props;
    const { BetType, Result } = bet;
    const { t } = useTranslation();
    const { Id: PlayerId } = useSelector(getMainPlayerState);
    const [seat, setSeat] = useState<number>();
    const [bankerPoint, setBankerPoint] = useState<string>();
    const [bankerCards, setBankerCards] = useState<number[]>();
    const [playerHand1Point, setPlayerHand1Point] = useState<string>();
    const [playerHand2Point, setPlayerHand2Point] = useState<string>();
    const [playerHand1Cards, setPlayerHand1Cards] = useState<number[]>();
    const [playerHand2Cards, setPlayerHand2Cards] = useState<number[]>();
    const [isHand1Highlight, setIsHand1Highlight] = useState<boolean>(false);
    const [isHand2Highlight, setIsHand2Highlight] = useState<boolean>(false);

    const getHandString = (position: number) => {
        if (playerHand2Cards) return `P${seat} #${position}`;
        return `P${seat}`;
    };

    useEffect(() => {
        let seat = ((BetType % 7) + 1) as BetRoomSeatIndex;
        const blackjackResult = JSON.parse(Result) as BlackjackResult;
        const bp = blackjackResult.B.R;
        let pp = blackjackResult.P.find(p => p.SN === seat)?.H;
        if (bet.BetType === BlackjackBetType.BJB_Insurance) {
            const target = blackjackResult.P.find(
                p => p.I === PlayerId.toString()
            );
            if (target) {
                pp = target.H;
                seat = target.SN;
            }
        }

        setSeat(getDisplaySeat(seat));
        setBankerPoint(
            bp === 255
                ? 'BJ'
                : getBlackjackPoints(blackjackResult.B.PK)[0].toString()
        );
        setBankerCards(blackjackResult.B.PK);
        if (pp) {
            const hand1 = pp.at(0);
            const hand2 = pp.at(1);
            if (hand2) {
                const points = getBlackjackPoints(hand2.PK);
                const p2 = hand2.R === 255 ? 'BJ' : points[0].toString();
                setPlayerHand2Point(p2);
                setPlayerHand2Cards(hand2.PK);
                setIsHand2Highlight(
                    BetType >= BlackjackBetType.BJB1_Split2 &&
                        BetType <= BlackjackBetType.BJB7_Split2
                );
            }
            if (hand1) {
                const points = getBlackjackPoints(hand1.PK);
                const p1 = hand1.R === 255 ? 'BJ' : points[0].toString();
                setPlayerHand1Point(p1);
                setPlayerHand1Cards(hand1.PK);
                if (hand2) {
                    setIsHand1Highlight(
                        BetType >= BlackjackBetType.BJB1_Split1 &&
                            BetType <= BlackjackBetType.BJB7_Split1
                    );
                }
            }
        }
    }, []);

    return (
        <Box className="blackjack-result-detail">
            <Box className="item-blackjack-hands">
                {playerHand1Cards && (
                    <>
                        <Box
                            className={
                                isHand1Highlight
                                    ? 'blackjack-indicator selected'
                                    : 'blackjack-indicator unselected'
                            }
                        >
                            {getHandString(1)}
                        </Box>
                        <Box className="blackjack-poker-list">
                            {playerHand1Cards.map((card, index) => (
                                <Box
                                    className="blackjack-pokers"
                                    key={`bj-ecard-h1-p${index}`}
                                    sx={{
                                        zIndex: index + 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {getPokerMin(card)}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
                <Box className="blackjack-points">{playerHand1Point}</Box>
            </Box>
            <Box className="item-blackjack-hands">
                {playerHand2Cards && (
                    <>
                        <Box
                            className={
                                isHand2Highlight
                                    ? 'blackjack-indicator selected'
                                    : 'blackjack-indicator unselected'
                            }
                        >
                            {getHandString(2)}
                        </Box>
                        <Box className="blackjack-poker-list">
                            {playerHand2Cards.map((card, index) => (
                                <Box
                                    className="blackjack-pokers"
                                    key={`bj-ecard-h2-p${index}`}
                                    sx={{
                                        zIndex: index + 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {getPokerMin(card)}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
                <Box className="blackjack-points">{playerHand2Point}</Box>
            </Box>
            <Box className="item-blackjack-hands">
                {bankerCards && (
                    <>
                        {t('tableinfo.dealer')}
                        <Box className="blackjack-poker-list">
                            {bankerCards.map((card, index) => (
                                <Box
                                    className="blackjack-pokers"
                                    key={`bj-ecard-b${index}`}
                                    sx={{
                                        zIndex: index + 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {getPokerMin(card)}
                                </Box>
                            ))}
                        </Box>
                    </>
                )}
                <Box className="blackjack-points">{bankerPoint}</Box>
            </Box>
        </Box>
    );
};
